/**
 * oms moduli uchun umumiy turlar/konstantalar (API + veb + mobil).
 * Faqat oms moduli egasi tahrirlaydi.
 *
 * Bu yerda faqat sof (side-effect'siz) funksiyalar: summa hisobi, birlamchi qiymatlar —
 * server ham, veb (jonli summa) ham aynan bir xil formulani ishlatadi.
 */
import type { L10n } from '../i18n';
import { ORDER_STATUSES, type FulfillmentType, type OrderStatus, type SalesChannel } from '../statuses';

// ------------------------------------------------------------------ Status tarixi manbalari
/** Status kim/nima tomonidan o'zgartirilgani (order_status_history.source). */
export const HISTORY_SOURCES = {
  user: { uz: 'Foydalanuvchi', ru: 'Пользователь', en: 'User', zh: '用户' },
  system: { uz: 'Tizim', ru: 'Система', en: 'System', zh: '系统' },
  wms: { uz: 'Ombor', ru: 'Склад', en: 'Warehouse', zh: '仓库' },
  delivery: { uz: 'Yetkazib berish', ru: 'Доставка', en: 'Delivery', zh: '配送' },
  integration: { uz: 'Integratsiya', ru: 'Интеграция', en: 'Integration', zh: '集成' },
  support: { uz: 'Call-markaz', ru: 'Колл-центр', en: 'Call centre', zh: '客服中心' },
} as const satisfies Record<string, L10n>;
export type HistorySource = keyof typeof HISTORY_SOURCES;
export const HISTORY_SOURCE_KEYS = Object.keys(HISTORY_SOURCES) as HistorySource[];

// ------------------------------------------------------------------ Kanallar tasnifi
/**
 * Marketplace kanallari: buyurtma integratsiya orqali keladi, yetkazishni marketplace bajaradi.
 * Yadro kanal NOMIGA qarab tarmoqlanmaydi — faqat shu tasnifga qaraydi (yangi marketplace = shu ro'yxatga qo'shish).
 */
export const MARKETPLACE_CHANNELS: readonly SalesChannel[] = ['uzum', 'wildberries', 'yandex'];
/** Hamkorning o'z kanallari — buyurtmani qo'lda yoki call-markazdan yaratish mumkin. */
export const OWN_CHANNELS: readonly SalesChannel[] = ['own_site', 'telegram', 'call_center', 'manual'];

export const isMarketplaceChannel = (channel: string): boolean => (MARKETPLACE_CHANNELS as readonly string[]).includes(channel);
export const isOwnChannel = (channel: string): boolean => (OWN_CHANNELS as readonly string[]).includes(channel);

/** O'z kanallari uchun ruxsat etilgan yetkazish turlari. */
export const OWN_FULFILLMENTS: readonly FulfillmentType[] = ['dbs', 'carrier', 'pickup'];
/** Marketplace kanallari uchun yetkazish turlari. */
export const MARKETPLACE_FULFILLMENTS: readonly FulfillmentType[] = ['fbs', 'fbo'];

// ------------------------------------------------------------------ Statuslar guruhlari
export const FINAL_ORDER_STATUSES = (Object.keys(ORDER_STATUSES) as OrderStatus[]).filter(
  (s) => (ORDER_STATUSES[s] as { final?: boolean }).final === true,
);
export const isFinalStatus = (s: string): boolean => (FINAL_ORDER_STATUSES as readonly string[]).includes(s);

/** Mijoz, manzil, sana, izohni tahrirlash mumkin (qadoqlanguncha). */
export const EDITABLE_STATUSES: readonly OrderStatus[] = ['new', 'on_hold', 'confirmed', 'picking'];
/**
 * Tarkibni (tovarlar) tahrirlash — faqat tasdiqlanguncha: tasdiqlangandan keyin ombor zaxira
 * qilgan bo'ladi, tarkib o'zgarishi uchun alohida hodisa kerak.
 */
export const ITEMS_EDITABLE_STATUSES: readonly OrderStatus[] = ['new', 'on_hold'];
/** Hamkor o'zi bekor qila oladi — jo'natilguncha. */
export const SELLER_CANCELABLE_STATUSES: readonly OrderStatus[] = ['new', 'on_hold', 'confirmed', 'picking', 'packed'];
/** Hali jo'natilmagan (yetkazish bekor qilinsa — buyurtma bekor, aks holda — qaytarish). */
export const NOT_SHIPPED_STATUSES: readonly OrderStatus[] = ['new', 'on_hold', 'confirmed', 'picking', 'packed'];

/** Ro'yxat tablari (status guruhlari). `all` — barchasi. */
export interface OrderTab {
  value: string;
  label: string;
  statuses: readonly OrderStatus[];
}
export const ORDER_TABS: readonly OrderTab[] = [
  { value: 'all', label: 'Hammasi', statuses: [] },
  { value: 'new', label: 'Yangi', statuses: ['new', 'on_hold'] },
  { value: 'confirmed', label: 'Tasdiqlangan', statuses: ['confirmed'] },
  { value: 'picking', label: 'Yig‘ilmoqda', statuses: ['picking'] },
  { value: 'packed', label: 'Qadoqlangan', statuses: ['packed'] },
  { value: 'shipped', label: 'Jo‘natilgan', statuses: ['shipped'] },
  { value: 'delivering', label: 'Yetkazilmoqda', statuses: ['delivering'] },
  { value: 'delivered', label: 'Yetkazildi', statuses: ['delivered'] },
  { value: 'returns', label: 'Qaytarish', statuses: ['returning', 'returned'] },
  { value: 'canceled', label: 'Bekor', statuses: ['canceled'] },
];

/** Ommaviy amallar. */
export const BULK_ORDER_ACTIONS = ['confirm', 'hold', 'cancel'] as const;
export type BulkOrderAction = (typeof BULK_ORDER_ACTIONS)[number];

// ------------------------------------------------------------------ Sabablar (UI tanlovlari)
/** Omborda tovar yetmasa avtomatik ushlab turish sababi. */
export const SHORTAGE_HOLD_REASON = 'Omborda yetarli emas';

export const HOLD_REASONS: readonly string[] = [
  'Mijoz bilan bog‘lanib bo‘lmadi',
  'Mijoz keyinroq tasdiqlaydi',
  'Manzil aniqlanmoqda',
  'To‘lov kutilmoqda',
  SHORTAGE_HOLD_REASON,
];

export const CANCEL_REASONS: readonly string[] = [
  'Mijoz fikridan qaytdi',
  'Telefon ko‘tarilmadi',
  'Boshqa joydan arzonroq topdi',
  'Yetkazish muddati to‘g‘ri kelmadi',
  'Tovar tugagan',
  'Dublikat buyurtma',
];

/** Yetkazish vaqt oraliqlari. */
export const DELIVERY_SLOTS: readonly string[] = ['09:00–13:00', '13:00–17:00', '17:00–21:00'];

/**
 * Yetkazish hududlari — delivery moduli `listZones()` mavjud bo'lmaganda zaxira ro'yxat.
 * `external` — tashqi kuryer xizmati orqali (hamkor kuryer) yetkaziladi.
 */
export interface ZoneOption {
  code: string;
  name: string;
  external: boolean;
}
export const FALLBACK_ZONES: readonly ZoneOption[] = [
  { code: 'chilonzor', name: 'Chilonzor', external: false },
  { code: 'yunusobod', name: 'Yunusobod', external: false },
  { code: 'mirzo_ulugbek', name: 'Mirzo Ulug‘bek', external: false },
  { code: 'yakkasaroy', name: 'Yakkasaroy', external: false },
  { code: 'shayxontohur', name: 'Shayxontohur', external: false },
  { code: 'olmazor', name: 'Olmazor', external: false },
  { code: 'sergeli', name: 'Sergeli', external: false },
  { code: 'yashnobod', name: 'Yashnobod', external: false },
  { code: 'mirobod', name: 'Mirobod', external: false },
  { code: 'uchtepa', name: 'Uchtepa', external: false },
  { code: 'bektemir', name: 'Bektemir', external: false },
  { code: 'toshkent_vil', name: 'Toshkent viloyati', external: true },
];

// ------------------------------------------------------------------ Summalar
export interface OrderLineInput {
  price: number;
  qty: number;
  /** Qator bo'yicha jami chegirma (so'm). */
  discount?: number | null;
}

export interface OrderTotals {
  itemsTotal: number;
  discountTotal: number;
  deliveryFee: number;
  total: number;
}

// ------------------------------------------------------------------ Yetkazish qatori
/**
 * MANBA QATORI YETKAZISHMI?
 *
 * DMS v8 va RetailCRM da yetkazish haqi ALOHIDA maydon emas — buyurtma tarkibidagi oddiy
 * qator («Доставка», «Yetkazib berish»). Keytosell'da esa u `orders.deliveryFee` — chunki
 * u tovar emas: omborga tushmaydi, yig'ilmaydi, zaxira qilinmaydi va qaytarishda alohida
 * qoida bilan hisoblanadi (`refundAmountOf`, `taskAmounts`).
 *
 * Shuning uchun kelgan qator shu yerda tanib olinadi va `deliveryFee` ga ko'chiriladi;
 * EKRANDA esa u o'z qatori bo'lib qoladi (DMS dagidek) — faqat tovar emas, yetkazish deb
 * belgilangan holda.
 *
 * Nom bo'yicha tanish — manbada boshqa belgi yo'q. Shuning uchun qoida TOR: faqat
 * yetkazishning o'zini bildiruvchi qisqa nomlar, «yetkazib berish uchun sumka» kabi
 * tovarlar tasodifan tushib qolmasin.
 */
const DELIVERY_TITLE_RE =
  /^(?:\p{Pd}|\s)*(?:dostavka|yetkaz(?:ib\s*berish|ish)|достав(?:ка|ки)|delivery|shipping|配送|运费)(?:\s*(?:xizmati|haqi|услуга|service|fee|cost))?(?:\s*[.!]*)$/iu;

export function isDeliveryTitle(title: string | null | undefined): boolean {
  const s = String(title ?? '').trim();
  return !!s && DELIVERY_TITLE_RE.test(s);
}

/** 2 xonagacha yaxlitlash (numeric(18,2)). */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const clampDiscount = (gross: number, discount: number | null | undefined) => Math.min(Math.max(0, discount ?? 0), Math.max(0, gross));

/** Qator summasi: narx × miqdor − chegirma (chegirma qator summasidan oshmaydi). */
export function lineTotal(line: OrderLineInput): number {
  const gross = round2(line.price * line.qty);
  return round2(gross - clampDiscount(gross, line.discount));
}

/** Buyurtma summalari: tovarlar (chegirmasiz), chegirmalar, yetkazish, jami. */
export function calcOrderTotals(lines: readonly OrderLineInput[], deliveryFee = 0): OrderTotals {
  let itemsTotal = 0;
  let discountTotal = 0;
  for (const l of lines) {
    const gross = round2(l.price * l.qty);
    itemsTotal += gross;
    discountTotal += clampDiscount(gross, l.discount);
  }
  itemsTotal = round2(itemsTotal);
  discountTotal = round2(discountTotal);
  const fee = round2(Math.max(0, deliveryFee || 0));
  return { itemsTotal, discountTotal, deliveryFee: fee, total: round2(itemsTotal - discountTotal + fee) };
}

/** Yetkazilgan qator: sotilgan miqdor va yetkazishda berilgan chegirma bilan. */
export interface DeliveredLineInput extends OrderLineInput {
  /** Sotilgan (yetkazilgan) miqdor; `null` — to'liq. */
  deliveredQty?: number | null;
  /** Yetkazishda berilgan chegirma, qator bo'yicha jami so'm; manfiy — antichegirma (ustama). */
  extraDiscount?: number | null;
}

/**
 * Yetkazish chegirmasining hisobga olinadigan qismi (DMS v8 0047 + 0062 qoidasi — YAGONA joy:
 * delivery, oms va finance shu funksiyani ishlatadi, summalar ayrilib ketmasin):
 *  - sotilmagan qatorda (miqdor 0) — 0: chegirma ham, antichegirma ham hisoblanmaydi;
 *  - musbat — sotilgan tovar sof summasidan oshmaydi (summa manfiyga tushmaydi);
 *  - manfiy (antichegirma) — CHEKLANMAYDI (narxi 0 bo'lgan qatorga ustama — eng ko'p holat).
 */
export function effectiveExtraDiscount(soldNet: number, soldQty: number, extra: number | null | undefined): number {
  const v = round2(Number(extra) || 0);
  if (!(soldQty > 0)) return 0;
  if (v < 0) return v;
  return Math.min(v, Math.max(0, round2(soldNet)));
}

/** Yetkazilgan miqdor (0..qty); `null` — to'liq. */
export const deliveredQtyOf = (l: Pick<DeliveredLineInput, 'qty' | 'deliveredQty'>): number =>
  Math.min(Math.max(0, l.deliveredQty ?? l.qty), l.qty);

/**
 * Yetkazilgan qator sof summasi: narx × sotilgan − buyurtma chegirmasi (mutanosib) − yetkazish
 * chegirmasi (`effectiveExtraDiscount`). Antichegirmada qator summasi narx × miqdordan oshadi.
 */
export function deliveredLineNet(l: DeliveredLineInput): number {
  const dq = deliveredQtyOf(l);
  const gross = round2(l.price * l.qty);
  const disc = l.qty > 0 ? round2((clampDiscount(gross, l.discount) * dq) / l.qty) : 0;
  const soldNet = round2(l.price * dq - disc);
  return round2(soldNet - effectiveExtraDiscount(soldNet, dq, l.extraDiscount));
}

/**
 * Yetkazilgan buyurtma summalari: har qatorda sotilgan miqdor bo'yicha, buyurtma chegirmasi
 * mutanosib, yetkazish chegirmasi (`extraDiscount`) qo'shilgan. `discountTotal` antichegirmada
 * manfiy bo'lishi mumkin (jami summa oshadi).
 */
export function calcDeliveredTotals(lines: readonly DeliveredLineInput[], deliveryFee = 0): OrderTotals {
  let itemsTotal = 0;
  let discountTotal = 0;
  for (const l of lines) {
    const gross = round2(l.price * deliveredQtyOf(l));
    itemsTotal += gross;
    discountTotal += round2(gross - deliveredLineNet(l));
  }
  itemsTotal = round2(itemsTotal);
  discountTotal = round2(discountTotal);
  const fee = round2(Math.max(0, deliveryFee || 0));
  return { itemsTotal, discountTotal, deliveryFee: fee, total: round2(itemsTotal - discountTotal + fee) };
}

/** Birlamchi yetkazish narxi (hudud stavkalari delivery modulida; bu — buyurtma uchun boshlang'ich qiymat). */
export const DEFAULT_DELIVERY_FEE = 20_000;
/** Shu summadan (chegirmadan keyin) yetkazish bepul. */
export const FREE_DELIVERY_FROM = 300_000;

export function defaultDeliveryFee(fulfillment: FulfillmentType, netItems: number): number {
  if (fulfillment !== 'dbs' && fulfillment !== 'carrier') return 0;
  return netItems >= FREE_DELIVERY_FROM ? 0 : DEFAULT_DELIVERY_FEE;
}

/**
 * Yetkazish turini aniqlash:
 *  - marketplace kanali → fbs (yoki so'ralgan bo'lsa fbo);
 *  - o'z kanallari → so'ralgan pickup/carrier, aks holda hudud tashqi bo'lsa carrier, qolgani dbs.
 */
export function defaultFulfillment(
  channel: string,
  opts: { requested?: FulfillmentType | null; zoneExternal?: boolean } = {},
): FulfillmentType {
  if (isMarketplaceChannel(channel)) return opts.requested === 'fbo' ? 'fbo' : 'fbs';
  if (opts.requested === 'pickup' || opts.requested === 'carrier') return opts.requested;
  if (opts.zoneExternal) return 'carrier';
  return 'dbs';
}

/** Kuryer/xizmat orqali manzilga yetkaziladimi (manzil va sana majburiy). */
export const needsAddress = (fulfillment: FulfillmentType): boolean => fulfillment === 'dbs' || fulfillment === 'carrier';

// ------------------------------------------------------------------ Eksport
export const ORDER_EXPORT_FIELDS = [
  { key: 'number', label: 'Raqam' },
  { key: 'externalId', label: 'Tashqi ID' },
  { key: 'createdAt', label: 'Yaratilgan vaqt' },
  { key: 'seller', label: 'Hamkor' },
  { key: 'channel', label: 'Kanal' },
  { key: 'fulfillment', label: 'Yetkazish turi' },
  { key: 'status', label: 'Holat' },
  { key: 'customerName', label: 'Mijoz' },
  { key: 'customerPhone', label: 'Telefon' },
  { key: 'deliveryZone', label: 'Hudud' },
  { key: 'deliveryAddress', label: 'Manzil' },
  { key: 'deliveryDate', label: 'Yetkazish sanasi' },
  { key: 'deliverySlot', label: 'Vaqt oralig‘i' },
  { key: 'items', label: 'Tarkib' },
  { key: 'itemsCount', label: 'Dona' },
  { key: 'itemsTotal', label: 'Tovarlar summasi' },
  { key: 'discountTotal', label: 'Chegirma' },
  { key: 'deliveryFee', label: 'Yetkazish narxi' },
  { key: 'total', label: 'Jami' },
  { key: 'paymentMethod', label: 'To‘lov usuli' },
  { key: 'paymentSystem', label: 'To‘lov tizimi' },
  { key: 'paymentStatus', label: 'To‘lov holati' },
  { key: 'comment', label: 'Izoh' },
  { key: 'holdReason', label: 'Ushlab turish sababi' },
  { key: 'confirmedAt', label: 'Tasdiqlangan' },
  { key: 'shippedAt', label: 'Jo‘natilgan' },
  { key: 'deliveredAt', label: 'Yetkazilgan' },
  { key: 'canceledAt', label: 'Bekor qilingan' },
  { key: 'cancelReason', label: 'Bekor sababi' },
] as const;
export type OrderExportField = (typeof ORDER_EXPORT_FIELDS)[number]['key'];
export const ORDER_EXPORT_FIELD_KEYS = ORDER_EXPORT_FIELDS.map((f) => f.key) as OrderExportField[];
export const DEFAULT_EXPORT_FIELDS: OrderExportField[] = [
  'number',
  'createdAt',
  'seller',
  'channel',
  'status',
  'customerName',
  'customerPhone',
  'deliveryZone',
  'itemsCount',
  'total',
  'paymentMethod',
];
/** Bir eksportdagi maksimal qatorlar (katta hajm — fon hisoboti orqali). */
export const ORDER_EXPORT_MAX_ROWS = 20_000;
/** Eksport shablonlari moduli (`/export-templates?module=orders`). */
export const ORDER_EXPORT_MODULE = 'orders';
